import Alert from '@mui/material/Alert/Alert'
import AlertTitle from '@mui/material/AlertTitle/AlertTitle'
import { FC, useEffect, useMemo, useState } from 'react'
import { CONFIG } from '../../../config'
import { IFoundProductData } from '../../../model/IFoundProduct'
import { IConfigurationType, IPriceType } from '../../../model/IImport'
import {
  ICategoryIdsItem,
  IFoundProduct,
  IImportForm,
  IImportFormProduct,
  IProductSetting,
} from '../../../model/IImportForm'
import { IStateError } from '../../../model/IStateError'
import useImport from '../../hooks/useImport'
import Button from '../form/Button'
import FormBlock from '../form/FormBlock'
import { TInputEvent } from '../form/Input'
import InputIconEraser from '../form/InputIconEraser'
import SwitchGroup from '../form/SwitchGroup'
import CategoriesTree from '../magento/CategoriesTree'
import Switch from '../form/Switch'
import { settingsApi } from '../../services/settings'
import { ISettings } from '../../../model/ISettings'
import Loading from '../common/loading/Loading'
import useSettingsList from '../../hooks/useSettingsList'

const defaultFoundProduct: IFoundProduct = {
  foundProductId: '',
  foundProductName: '',
  foundProductError: '',
}

const defaultProductSetting: IProductSetting = {
  isImprtName: false,
  isImprtOptions: false,
  isImprtPrice: false,
  isImprtStock: false,
  isImprtRewriteImages: false,
}

const defaultProductState: IImportFormProduct = {
  productId: '',
  company: 'pcna',
  ...defaultFoundProduct,
  ...defaultProductSetting,
}

const defaultState: IImportForm = {
  products: [defaultProductState],
  configurationType: 'Decorated',
  priceType: 'List',
  categoryIds: [],
  goalServer: 'magento.lan',
}

const ImportForm: FC<{
  stateErrs?: IStateError<IImportForm>
  onSubmit: (state: IImportForm) => unknown
  loading?: boolean
}> = ({ stateErrs = {}, onSubmit, loading }) => {
  const { toFindProduct } = useImport()
  const { settingsList } = useSettingsList()

  // const [state, setState] = useState<IImportForm>(defaultState)
  const [state, setState] = useState<IImportForm | undefined>()
  const [productIndxsLoading, setProductIndxsLoading] = useState<number[]>([])

  // --
  // -- Set initial product setting by comparing base settings with default settings
  // --
  const getBaseSettingsForProduct = (settings: ISettings | undefined): IProductSetting => {
    const productSettingNames = Object.keys(defaultProductSetting) as (keyof IProductSetting)[]
    const existedBaseSetting = (settings?.import || []).reduce(
      (acc, { name, value }) => ({
        ...acc,
        ...(productSettingNames.includes(name as keyof IProductSetting) ? { [name]: value } : {}),
      }),
      {} as Partial<IProductSetting>,
    )

    return Object.entries(defaultProductSetting).reduce(
      (acc, [name, defaultValue]) => ({
        ...acc,
        [name]:
          existedBaseSetting?.[name as keyof IProductSetting] !== undefined
            ? !!+existedBaseSetting[name as keyof IProductSetting]!
            : defaultValue,
      }),
      {} as IProductSetting,
    )
  }
  // --
  const { data: settings } = settingsApi.useGetSettingsQuery({})
  // --
  const initState: IImportForm | undefined = useMemo(
    () =>
      settings && {
        ...defaultState,
        products: [
          {
            ...defaultProductState,
            ...getBaseSettingsForProduct(settings),
          },
        ],
      },
    [settings],
  )
  // --
  useEffect(() => {
    initState && setState(initState)
  }, [initState])
  // --

  const handleReset = () => setState(() => initState)
  const handleSubmit = () => state && onSubmit(state)

  const handleProduct =
    (
      key: keyof IImportFormProduct,
      indx: number,
    ): ((e: TInputEvent | string | number | boolean) => unknown) =>
    (e) =>
      setState((prevState) => {
        if (!prevState) return prevState
        const value = (e as TInputEvent)?.target?.value ?? e ?? ''
        return {
          ...prevState,
          products: [
            ...prevState.products.slice(0, indx),
            {
              ...prevState.products[indx],
              [key]: value,
              ...(!value ? defaultFoundProduct : {}),
            },
            ...prevState.products.slice(indx + 1),
          ],
        }
      })

  const setFoundProduct = (indx: number, foundProduct: IFoundProduct) =>
    state &&
    state.products?.[indx] &&
    setState(
      (prevState) =>
        prevState && {
          ...prevState,
          products: [
            ...prevState.products.slice(0, indx),
            {
              ...prevState.products[indx],
              ...foundProduct,
            },
            ...prevState.products.slice(indx + 1),
          ],
        },
    )

  const deleteFoundProduct = (indx: number, foundProductError: string = '') =>
    setFoundProduct(indx, {
      ...defaultFoundProduct,
      foundProductError,
    })

  // --
  // -- Methods
  // --
  const addNewProductForm = () =>
    setState(
      (prevState) =>
        prevState &&
        initState?.products[0] && {
          ...prevState,
          products: [...prevState.products, initState?.products[0]],
        },
    )

  const deleteProductForm = (indx: number) =>
    state &&
    state.products.length > 1 &&
    indx < state.products.length &&
    // && false
    setState(
      (prevState) =>
        prevState && {
          ...prevState,
          products: [...prevState.products.slice(0, indx), ...prevState.products.slice(indx + 1)],
        },
    )

  const findProductByIdAndCompany = async (indx: number) => {
    if (!state?.products?.[indx]) return
    if (!state.products[indx].productId.trim()) return deleteFoundProduct(indx)

    setProductIndxsLoading((prevState) => [...prevState, indx])
    const foundProduct = await toFindProduct(state.products?.[indx])
    setProductIndxsLoading((prevState) => prevState.filter((item) => item !== indx))

    if ((foundProduct as IFoundProduct)?.foundProductError)
      return deleteFoundProduct(indx, (foundProduct as IFoundProduct).foundProductError)

    setFoundProduct(indx, {
      foundProductId: foundProduct?.productId ?? '123',
      foundProductName: (foundProduct as IFoundProductData)?.productName ?? '',
      foundProductError: '',
    })
  }

  const isReadyToSubmit = (): boolean =>
    !loading &&
    !productIndxsLoading.length &&
    !!state?.products?.find?.(({ foundProductId }) => foundProductId)
  // && !!state?.categoryIds?.length

  // --
  // -- Effects
  // --
  // -- Add new product form
  useEffect(() => {
    state && state.products[state.products.length - 1]?.productId && addNewProductForm()
  }, [state?.products?.[(state?.products?.length || 0) - 1]?.productId])

  // -- Delete last product form
  useEffect(() => {
    state &&
      !state.products[state.products.length - 1]?.productId &&
      !state.products[state.products.length - 2]?.productId &&
      deleteProductForm(state.products.length - 1)
  }, [
    state?.products?.[(state?.products?.length || 0) - 1]?.productId,
    state?.products?.[(state?.products?.length || 0) - 2]?.productId,
  ])

  return !state ? (
    <Loading />
  ) : (
    <div className='space-y-12'>
      <FormBlock
        title='Products'
        subTitle='ProductParameters'
      >
        {state.products.map(
          ({ productId, company, foundProductId, foundProductName, foundProductError }, indx) => (
            <div
              key={`product-${indx}`}
              className={[
                'col-span-12 grid grid-cols-12 gap-x-6 gap-y-2 bg-slate-50 p-4 px-6 border-[1px] rounded-md shadow-sm',
                productIndxsLoading.includes(indx) ? 'animate animate-pulse' : '',
              ].join(' ')}
            >
              <div className='col-span-6'>
                <InputIconEraser
                  icon='hash'
                  name={`productId-${indx}`}
                  label={`${indx + 1}. Product ID`}
                  value={productId || ''}
                  handle={handleProduct('productId', indx)}
                  disabled={loading || productIndxsLoading.includes(indx)}
                  error={stateErrs?.products?.[indx]}
                  description={`Product Id in the ${CONFIG.companies[company]} company`}
                  onBlur={() => findProductByIdAndCompany(indx)}
                />
              </div>

              <div className='col-span-6'>
                <SwitchGroup
                  label='Source Company'
                  name='company'
                  size='small'
                  value={company}
                  handleToggle={(e) => {
                    handleProduct('company', indx)(e)
                    findProductByIdAndCompany(indx)
                  }}
                  disabled={loading || productIndxsLoading.includes(indx)}
                  options={Object.entries(CONFIG.companies).map(([value, title]) => ({
                    title,
                    value,
                  }))}
                  className='flex flex-col gap-3'
                />
              </div>

              {!!Object.keys(settingsList).length && (
                <div className='col-span-12 grid grid-cols-12 gap-x-5 gap-y-1'>
                  {Object.keys(defaultProductSetting).map((key) => (
                    <Switch
                      key={`product-${indx}-${key}`}
                      name={key}
                      label={settingsList?.[key]?.title || key}
                      size='tiny'
                      className='col-span-4'
                      variant='pale'
                      value={state.products[indx][key as keyof IProductSetting]}
                      handleToggle={() =>
                        handleProduct(
                          key as keyof IProductSetting,
                          indx,
                        )(!state.products[indx][key as keyof IProductSetting])
                      }
                      disabled={loading || productIndxsLoading.includes(indx)}
                    />
                  ))}
                </div>
              )}

              {!!foundProductError && (
                <Alert
                  severity='error'
                  className='col-span-12 mt-4'
                >
                  <AlertTitle>{foundProductError}</AlertTitle>
                </Alert>
              )}

              {!!foundProductId && (
                <Alert
                  severity='success'
                  className='col-span-12 mt-4'
                >
                  <AlertTitle>Product "{productId}" was found</AlertTitle>#{foundProductId}{' '}
                  <b>{foundProductName}</b>
                </Alert>
              )}
            </div>
          ),
        )}
      </FormBlock>

      <FormBlock
        title='Settings'
        subTitle='Main import settings'
        // className='bg-slate-100'
      >
        <SwitchGroup
          size='small'
          label='Configuration Type'
          name='configurationType'
          value={state.configurationType}
          handleToggle={(value) =>
            setState(
              (prevState) =>
                prevState && {
                  ...prevState,
                  configurationType: value as IConfigurationType,
                },
            )
          }
          options={Object.entries(CONFIG.configurationTypes).map(([value, title]) => ({
            title,
            value,
          }))}
          className='col-span-6 flex flex-col gap-6'
        />

        <SwitchGroup
          size='small'
          label='Price Type'
          name='priceType'
          value={state.priceType}
          handleToggle={(value) =>
            setState(
              (prevState) =>
                prevState && {
                  ...prevState,
                  priceType: value as IPriceType,
                },
            )
          }
          options={Object.entries(CONFIG.priceTypes).map(([value, title]) => ({ title, value }))}
          className='col-span-6 flex flex-col gap-6'
        />
      </FormBlock>

      <FormBlock
        title='Categories'
        subTitle='Choose categories or leave original ones'
      >
        <div
          className='
          p-8 col-span-12 flex gap-6 flex-wrap  
          [&>ul]:w-[18em] [&>ul]:bg-white [&>ul]:border-[1px] [&>ul]:rounded-md [&>ul]:shadow-md
          [&>ul]:py-4
        '
        >
          <CategoriesTree
            categoryIds={state.categoryIds}
            setCategoryIds={(categoryIds: ICategoryIdsItem[]) =>
              setState(
                (prevState) =>
                  prevState && {
                    ...prevState,
                    categoryIds,
                  },
              )
            }
          />
        </div>
      </FormBlock>

      <div className='fixed bottom-0 right-0 w-[100%] bg-white px-8 py-6 border-t-[1px]'>
        <div className='flex justify-end gap-4 '>
          <Button
            isNotFullWidth
            onClick={handleReset}
            disabled={loading || !!productIndxsLoading?.length}
          >
            Reset
          </Button>
          <Button
            isNotFullWidth
            type='submit'
            onClick={handleSubmit}
            disabled={!isReadyToSubmit()}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ImportForm
